// FreeBookPage — one page per book; a campaign is a link (thread
// mct_55e916be-cda; design docs/plans/free-book-campaign-pages-2026-10-11.md).
//
//   /free-book/:book                       — the-successful-bookkeeper, the-e-myth-bookkeeper, ...
//   /free-book/:book?from=<giver>&campaign=<key>
//                                          — the gift link's landing: "A gift from Ron Bester",
//                                            Ron's photo and note, the campaign's headline or
//                                            video when it has one. Unknown values render the
//                                            generic page; nothing personal ever rides in the URL.
//   /free-book/na|au                       — the two legacy region aliases (the TSB book);
//                                            with ?embed=1 they still render the bare form the
//                                            HubSpot pages iframe (FreeBookFormPage) until the
//                                            three website redirects land.
//
// The country decides print (US, CA, AU, UK) or ebook, and the region the
// claim passes on to. The two business questions are asked of everyone.
// After a claim the thank-you offers a call: the giver's own booking page when
// they take calls in the Engine, else Ron's, on this same site so the campaign
// tag rides along to the booking (booking-source.ts counts it).
//
// Landing-views beacon and GTM mount here (sop-ee-anon-visitor-attribution
// checklist); the claim POST carries the attribution payload so the campaign
// reaches the ledger row, the identity event and the visitor tie.

import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import TakerShell from './components/TakerShell';
import FreeBookFormPage from './FreeBookFormPage';
import { useBrandGtm } from './lib/useBrandGtm';
import { recordLandingView } from './api';
import { getAttributionPayload } from './attribution';

const API = import.meta.env.VITE_API_BASE_URL as string;

interface Choice { value: string; label: string }
interface Form {
  heading: string; intro: string; countryLabel: string; countryHelp: string; countries: Choice[]; otherCountryValue: string;
  businessStages: Choice[] | null; describesYou: Choice[] | null; submitLabel: string;
}
interface Giver { slug: string; name: string; firstName: string | null; role: string | null; photoUrl: string | null }
interface Call { personSlug: string; personName: string; firstName: string | null; path: string }
interface PagePayload {
  book: { code: string; name: string; slug: string; path: string | null };
  form: Form;
  campaign: { key: string; headline: string | null; giverNote: string | null; videoUrl: string | null } | null;
  giver: Giver | null;
  call: Call | null;
}
interface Done { heading: string; body: string; ebook: boolean; call: Call | null; giver: { name: string; firstName: string | null } | null }

const h1: CSSProperties = { fontFamily: "'Fraunces', serif", fontSize: 34, fontWeight: 900, color: '#0A0A2E', lineHeight: 1.15, margin: '0 0 12px' };
const lead: CSSProperties = { fontSize: 17, color: '#44445e', lineHeight: 1.55, margin: '0 0 24px' };
const card: CSSProperties = { background: '#fff', borderRadius: 16, padding: '22px 22px 24px', boxShadow: '0 4px 24px rgba(10,10,46,0.06)' };
const label: CSSProperties = { display: 'block', fontWeight: 600, fontSize: 15, color: '#0A0A2E', marginBottom: 6 };
const input: CSSProperties = { width: '100%', boxSizing: 'border-box', fontSize: 16, padding: '12px 14px', borderRadius: 10, border: '1px solid #d6d3cc', background: '#fff', fontFamily: 'inherit' };
const btn: CSSProperties = { display: 'inline-block', background: '#F05A28', color: '#fff', border: 'none', borderRadius: 10, padding: '13px 22px', fontWeight: 700, fontSize: 16, cursor: 'pointer', textDecoration: 'none' };
const row: CSSProperties = { marginBottom: 16 };
const help: CSSProperties = { fontSize: 13, color: '#6b6b80', margin: '-2px 0 6px' };
const grid: CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0 14px' };
const giftCard: CSSProperties = { ...card, display: 'flex', gap: 18, alignItems: 'center', marginBottom: 26, background: '#FFF7F3', boxShadow: 'none', border: '1px solid #F8D5C6' };

type TextKey = 'firstName' | 'lastName' | 'email' | 'company' | 'phone' | 'address1' | 'address2' | 'city' | 'state' | 'postalCode' | 'countryOther';

/** Only an embeddable player URL goes in a frame; anything else is a plain link. */
function embedSrc(url: string | null): string | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    const h = u.hostname.replace(/^www\./, '');
    if (h === 'youtube.com' || h === 'youtu.be') {
      const id = h === 'youtu.be' ? u.pathname.slice(1) : (u.searchParams.get('v') ?? u.pathname.split('/embed/')[1] ?? '');
      return id ? `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}` : null;
    }
    if (h === 'vimeo.com') { const id = u.pathname.split('/').filter(Boolean)[0]; return id ? `https://player.vimeo.com/video/${encodeURIComponent(id)}` : null; }
    if (h === 'iframe.mediadelivery.net') return url;
  } catch { /* fall through */ }
  return null;
}

/** The call link on this same site, carrying the link's tags (and the campaign
 *  when the tags did not) so the booking attributes to the campaign. */
function callHref(call: Call, campaignKey: string | null, search: URLSearchParams): string {
  const p = new URLSearchParams();
  for (const k of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']) { const v = search.get(k); if (v) p.set(k, v); }
  if (campaignKey && !p.get('utm_campaign')) p.set('utm_campaign', campaignKey);
  if (campaignKey && !p.get('utm_source')) p.set('utm_source', 'free-book');
  const q = p.toString();
  return q ? `${call.path}?${q}` : call.path;
}

export default function FreeBookPage() {
  const { book = '' } = useParams();
  const [params] = useSearchParams();
  const embed = params.get('embed') === '1';
  // The HubSpot pages still iframe the bare regional form until the redirects land.
  if (embed && (book === 'na' || book === 'au')) return <FreeBookFormPage />;
  return <BookPage book={book} params={params} />;
}

function BookPage({ book, params }: { book: string; params: URLSearchParams }) {
  const campaignParam = params.get('campaign');
  const fromParam = params.get('from');
  const [page, setPage] = useState<PagePayload | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'missing' | 'error'>('loading');
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState<Done | null>(null);
  const started = useRef(Date.now());
  const hp = useRef<HTMLInputElement>(null);

  useBrandGtm();
  // Landing-views beacon: a marketing landing reached from a share link (sop-ee-anon-visitor-attribution checklist).
  useEffect(() => { if (book) recordLandingView(`free-book-${book}`); }, [book]);

  useEffect(() => {
    const q = new URLSearchParams();
    if (campaignParam) q.set('campaign', campaignParam);
    if (fromParam) q.set('from', fromParam);
    const qs = q.toString();
    fetch(`${API}/api/public/books/free-book/${encodeURIComponent(book)}${qs ? `?${qs}` : ''}`)
      .then(async (r) => {
        if (r.status === 404) { setState('missing'); return; }
        if (!r.ok) { setState('error'); return; }
        setPage((await r.json()) as PagePayload);
        setState('ready');
      })
      .catch(() => setState('error'));
  }, [book, campaignParam, fromParam]);

  const videoSrc = useMemo(() => embedSrc(page?.campaign?.videoUrl ?? null), [page?.campaign?.videoUrl]);
  const set = (k: string, v: string) => setValues((cur) => ({ ...cur, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!page) return;
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${API}/api/public/books/free-book/${encodeURIComponent(book)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...values, campaign: campaignParam ?? undefined, from: fromParam ?? undefined, pageUrl: window.location.href,
          hp: hp.current?.value ?? '', formMs: Date.now() - started.current, attribution: getAttributionPayload(),
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(d?.error ?? `Something went wrong (${r.status}). Please try again.`); return; }
      setDone({ heading: d.thanksHeading, body: d.thanksBody, ebook: !!d.ebook, call: d.call ?? null, giver: d.giver ?? null });
      window.scrollTo(0, 0);
    } catch {
      setErr('We could not reach our system. Please try again in a moment.');
    } finally { setBusy(false); }
  };

  const text = (k: TextKey, lbl: string, opts: { required?: boolean; type?: string; auto?: string; max?: number } = {}) => (
    <div style={row}>
      <label htmlFor={`fb-${k}`} style={label}>{lbl}{opts.required === false ? '' : ' *'}</label>
      <input id={`fb-${k}`} type={opts.type ?? 'text'} required={opts.required !== false} autoComplete={opts.auto} maxLength={opts.max ?? 200} style={input}
        value={values[k] ?? ''} onChange={(e) => set(k, e.target.value)} />
    </div>
  );
  const select = (k: string, lbl: string, choices: Choice[], helpText?: string) => (
    <div style={row}>
      <label htmlFor={`fb-${k}`} style={label}>{lbl} *</label>
      {helpText && <p style={help}>{helpText}</p>}
      <select id={`fb-${k}`} required style={input} value={values[k] ?? ''} onChange={(e) => set(k, e.target.value)}>
        <option value="" disabled>Please select</option>
        {choices.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );

  let body: React.ReactNode;
  if (state === 'loading') body = <p style={lead}>Loading…</p>;
  else if (state === 'missing') body = <p style={lead}>We could not find that book.</p>;
  else if (state === 'error' || !page) body = <p style={lead}>Something went wrong loading this page. Please refresh, or email support@thesuccessfulbookkeeper.com.</p>;
  else if (done) {
    const who = done.call?.firstName ?? done.call?.personName ?? null;
    body = (
      <>
        <div style={card} role="status">
          <h1 style={{ ...h1, fontSize: 28 }}>{done.heading}</h1>
          <p style={{ ...lead, margin: 0 }}>{done.body}</p>
        </div>
        {done.call && who && (
          <div style={{ ...card, marginTop: 18, background: '#0A0A2E', color: '#fff' }}>
            <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 24, fontWeight: 900, margin: '0 0 8px', color: '#fff' }}>
              While your book is on its way, would you like 20 minutes with {who}?
            </h2>
            <p style={{ ...lead, color: '#d9d9e6', margin: '0 0 18px' }}>
              A short, friendly call about where your bookkeeping business is and what would make the next year easier. No pitch, just a conversation.
            </p>
            <a href={callHref(done.call, page.campaign?.key ?? null, params)} style={btn}>Book a time with {who}</a>
          </div>
        )}
      </>
    );
  } else {
    const giver = page.giver;
    const giverFirst = giver?.firstName ?? giver?.name ?? null;
    const headline = page.campaign?.headline ?? (giver ? `${giverFirst} would like to send you a copy of ${page.book.name}` : page.form.heading);
    body = (
      <>
        {giver && (
          <div style={giftCard}>
            {giver.photoUrl && <img src={giver.photoUrl} alt={giver.name} style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', flex: '0 0 auto' }} />}
            <div>
              <p style={{ margin: 0, fontSize: 13, letterSpacing: 0.6, textTransform: 'uppercase', color: '#F05A28', fontWeight: 700 }}>A gift from {giver.name}</p>
              {page.campaign?.giverNote
                ? <p style={{ margin: '6px 0 0', fontSize: 16, color: '#0A0A2E', lineHeight: 1.5 }}>{page.campaign.giverNote}</p>
                : <p style={{ margin: '6px 0 0', fontSize: 16, color: '#0A0A2E', lineHeight: 1.5 }}>{giverFirst} is sending this book on to the people who will get the most from it. Tell us where to post your copy.</p>}
              {giver.role && <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b6b80' }}>{giver.name}, {giver.role}</p>}
            </div>
          </div>
        )}
        <h1 style={h1}>{headline}</h1>
        <p style={lead}>{page.form.intro}</p>
        {videoSrc && (
          <div style={{ position: 'relative', paddingTop: '56.25%', borderRadius: 14, overflow: 'hidden', marginBottom: 24, background: '#000' }}>
            <iframe src={videoSrc} title="A message about the book" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }} />
          </div>
        )}
        <form onSubmit={submit} style={card}>
          <div style={grid}>
            {text('firstName', 'First name', { auto: 'given-name', max: 80 })}
            {text('lastName', 'Last name', { auto: 'family-name', max: 80 })}
          </div>
          {text('email', 'Email', { type: 'email', auto: 'email' })}
          {text('company', 'Company name', { auto: 'organization', max: 160 })}
          {select('country', page.form.countryLabel, page.form.countries, page.form.countryHelp)}
          {values.country === page.form.otherCountryValue ? (
            text('countryOther', 'Which country are you in?', { auto: 'country-name', max: 80 })
          ) : (
            <>
              {text('address1', 'Street address', { auto: 'address-line1' })}
              {text('address2', 'Street address 2', { required: false, auto: 'address-line2' })}
              <div style={grid}>
                {text('city', 'City', { auto: 'address-level2', max: 100 })}
                {text('state', 'State / Region', { auto: 'address-level1', max: 100 })}
              </div>
              <div style={grid}>
                {text('postalCode', 'Postal code', { auto: 'postal-code', max: 20 })}
                {text('phone', 'Phone number', { type: 'tel', auto: 'tel', max: 40 })}
              </div>
            </>
          )}
          {page.form.businessStages && select('businessStage', 'What is the current stage of your bookkeeping business?', page.form.businessStages)}
          {page.form.describesYou && select('describesYou', 'Which of these statements describes you best right now?', page.form.describesYou)}
          {/* Honeypot: hidden from people, filled by bots. */}
          <div aria-hidden="true" style={{ position: 'absolute', left: -10000, width: 1, height: 1, overflow: 'hidden' }}>
            <label htmlFor="fb-website">Website</label>
            <input id="fb-website" ref={hp} tabIndex={-1} autoComplete="off" name="website" />
          </div>
          <button type="submit" disabled={busy} style={{ ...btn, opacity: busy ? 0.6 : 1 }}>{busy ? 'Sending…' : page.form.submitLabel}</button>
          {err && <p role="alert" style={{ color: '#B42318', fontSize: 15, margin: '12px 0 0' }}>{err}</p>}
        </form>
      </>
    );
  }

  return (
    <TakerShell footerLabel="The Successful Bookkeeper">
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '44px 16px 80px' }}>{body}</div>
    </TakerShell>
  );
}
